// ---- Skill + Standing specs (data) ----
// The progression engine's content shapes. Skills are shallow trees of boxes
// (qualitative unlocks) gated by per-bar XP; standing tracks are title ladders
// whose ranks mint influence. Both are CONTENT: authored as JSON in
// content/skills/*.json + content/standing/*.json, validated + hydrated by
// src/content/load.ts (mirrors quest/item/shop). DOM- and THREE-free so BOTH the
// client (Skills book HUD) and the headless server (XP grants, unlock validation)
// import the registry (src/skill/index.ts). `SkillDoc`/`StandingDoc` in
// src/content/schemas.ts are the zod mirrors of these shapes.

/** Passive stat bumps an unlocked box grants, summed server-side.
 *  `damage` = outgoing damage % bonus (25 → ×1.25 on weapon×mult; stacks additively).
 *  `armor`  = incoming damage % reduction (same units as wear.armor; stacks with gear,
 *  then shares the armorPct cap in damagePlayer). */
export type BoxStat = Partial<Record<"maxHp" | "damage" | "stamina" | "armor", number>>;

/** Fishing effects an unlocked box grants (plan 10; semantics remapped by the
 *  Living Waters rework) — folded by `fishingSum`. */
export type BoxFishing = {
  /** × the bite grab window AND the reel-minigame hit window (multiplies). */
  reelWindowMul?: number;
  /** + hook-tier access (sums) — the max fish `tier` that takes your hook. */
  tableTier?: number;
  /** + deployed nets allowed at once (sums). */
  netCount?: number;
  /** × the net soak time (multiplies — <1 is faster). */
  soakMul?: number;
};

/** Hunting effects an unlocked box grants (plan 11) — folded by `huntingSum`. */
export type BoxHunting = {
  /** + to the stalked shot's band hit% (sums; capped at the sim's AIM_HIT_CAP). */
  aimHitBonus?: number;
  /** + trophy-tier access (sums — scales rare-trophy odds via trophyChance). */
  trophyTier?: number;
  /** + deployed traps allowed at once (sums). */
  trapCount?: number;
  /** × the trap soak time (multiplies — <1 is faster). */
  soakMul?: number;
};

/** Cooking effects an unlocked box grants (plan 18/25) — folded by `cookingSum`. */
export type BoxCooking = {
  /** × the pan minigame's prompt hit window (multiplies across boxes — >1 is more forgiving). */
  panWindowMul?: number;
  /** + HP a cooked dish restores when eaten (sums; the payoff lands in-game). */
  healBonus?: number;
  /** + deployed fermenters allowed at once (sums). */
  brewCount?: number;
  /** × the brew soak time (multiplies — <1 is faster). */
  soakMul?: number;
};

/** One qualitative unlock in a skill tree. */
export type SkillBox = {
  id: string;
  name: string;
  branch?: string;
  requires: {
    /** Which xp bar gates this box (e.g. "melee"/"ranged"). */
    bar: string;
    xp: number;
    /** Box ids that must be unlocked first. */
    prereq?: string[];
    /** Fiction gate: must hold this track rank (top-tier boxes only). */
    rank?: { track: string; rank: number };
  };
  /** Influence cost (points are global/fungible — no per-track tag). */
  cost: number;
  grants: {
    /** Unlocks this attack style as an action-bar option (declarative; rare). */
    attack?: string;
    /** Weapon type this box lets you wield (item still needs `weapon.requiresBox`). */
    weaponType?: string;
    /**
     * Special-move id this box unlocks for the Journal chip.
     * Authoritative gate is still the move doc's `requiresBox` (must match this
     * box id); validate.ts cross-checks the pair.
     */
    move?: string;
    /** Passive stat bumps. */
    stat?: BoxStat;
    /** Fishing effects (plan 10) — the box's `branch` says which loop it aids. */
    fishing?: BoxFishing;
    /** Hunting effects (plan 11) — the box's `branch` says which loop it aids. */
    hunting?: BoxHunting;
    /** Cooking effects (plan 18) — the box's `branch` says which loop it aids. */
    cooking?: BoxCooking;
  };
};

export type SkillSpec = {
  id: string;
  name: string;
  /** Patron NPC spawn id + its standing track (recognition source). */
  patron: string;
  track: string;
  /** XP bars this skill grows (Combat = ["melee","ranged"]). */
  xpBars: string[];
  /** Per-skill investment cap (Combat is 12; many other skills still use 5). */
  capBoxes: number;
  branches?: string[];
  /** Per-bar XP curve: bar name → ascending thresholds (data; user-tunable). */
  thresholds?: Partial<Record<string, number[]>>;
  /** Optional display names for each threshold rung (same order/length as
   *  `thresholds[bar]`). Melee uses these in the Journal XP bar. */
  rungNames?: Partial<Record<string, string[]>>;
  boxes: SkillBox[];
};

/** One rung of a patron's title ladder. */
export type StandingRank = {
  /** The title shown when at this rank. */
  name: string;
  /** Cumulative meter needed to reach this rank. */
  progress: number;
  /** Influence minted into the global pool on reaching this rank. */
  grantsPoints: number;
};

export type StandingSpec = {
  id: string;
  name: string;
  /** Patron NPC spawn id. */
  patron: string;
  ranks: StandingRank[];
};

/** Look up a box in a skill by id (linear — trees are tiny). */
export function boxOf(skill: SkillSpec, id: string): SkillBox | undefined {
  return skill.boxes.find((b) => b.id === id);
}

/**
 * Display name for an XP amount on a bar (box gate or current progress).
 * Prefers an exact threshold match (box `requires.xp`); otherwise the highest
 * rung ≤ `xp`. Null when the skill has no `rungNames` for that bar — callers
 * fall back to a raw XP string. Shared by Journal gates + requirement lists.
 */
export function rungNameForXp(
  spec: SkillSpec,
  bar: string,
  xp: number,
): string | null {
  const thr = spec.thresholds?.[bar];
  const names = spec.rungNames?.[bar];
  if (!thr?.length || !names?.length) return null;
  const exact = thr.indexOf(xp);
  if (exact >= 0) return names[exact] ?? null;
  let idx = 0;
  for (let i = 0; i < thr.length; i++) {
    if (thr[i]! <= xp) idx = i;
  }
  return names[idx] ?? null;
}

/**
 * Influence already spent in this skill, counting **only boxes that still exist
 * in the skill doc**. Deleted/renamed ids (tree rewrites) must not count toward
 * `capBoxes` — server unlock and Journal both call this so they cannot drift.
 */
export function investedIn(spec: SkillSpec, unlocked: readonly string[]): number {
  let n = 0;
  for (const id of unlocked) {
    const b = boxOf(spec, id);
    if (b) n += b.cost;
  }
  return n;
}

/**
 * Drop unlocked box ids that no longer exist in the skill doc (after a tree
 * rewrite). Mutates `unlocked` in place; returns true if anything was removed.
 * Call on join / before unlock so ghosts cannot fill the invested list forever.
 */
export function scrubUnknownBoxes(spec: SkillSpec, unlocked: string[]): boolean {
  let changed = false;
  for (let i = unlocked.length - 1; i >= 0; i--) {
    if (!boxOf(spec, unlocked[i]!)) {
      unlocked.splice(i, 1);
      changed = true;
    }
  }
  return changed;
}

/** A player's progression state, as far as unlocking a box needs to see it. Both the
 *  server (live maps) and the client (mirror) build one of these so the eligibility
 *  rule lives in exactly ONE place — see `checkBox`. */
export type BoxProgress = {
  xp: (bar: string) => number;         // current xp on a bar
  hasBox: (boxId: string) => boolean;  // is a box (in THIS skill) already unlocked
  influence: number;                   // global spendable pool
  standingRank: (track: string) => number;
  invested: number;                    // points already invested in THIS skill
};

/** Which gate an unlock failed (or "unlocked" if already owned). The server ignores the
 *  detail (it just rejects); the client renders a reason from it. */
export type BoxGate = "unlocked" | "xp" | "prereq" | "rank" | "influence" | "cap";
export type BoxCheck =
  | { ok: true }
  | { ok: false; gate: "unlocked" }
  | { ok: false; gate: "xp"; bar: string; need: number }
  | { ok: false; gate: "prereq"; prereq: string }
  | { ok: false; gate: "rank"; track: string; rank: number }
  | { ok: false; gate: "influence" }
  | { ok: false; gate: "cap" };

/** The folded fishing effects of every unlocked box: multiplier fields multiply,
 *  the rest sum — over IDENTITY/ZERO bases, so the caller applies the fold onto
 *  its own defaults (biteWindow/hitWindow × mul, SOAK × mul, NET_COUNT + count, tier + n).
 *  ONE copy shared by the server's cast/deploy handlers and the client's slot
 *  UX (mirrors the checkBox discipline) — pass SKILLS + a hasBox predicate. */
export type FishingSum = { reelWindowMul: number; tableTier: number; netCount: number; soakMul: number };
export function fishingSum(skills: SkillSpec[], hasBox: (boxId: string) => boolean): FishingSum {
  const out: FishingSum = { reelWindowMul: 1, tableTier: 0, netCount: 0, soakMul: 1 };
  for (const spec of skills) {
    for (const b of spec.boxes) {
      const f = b.grants.fishing;
      if (!f || !hasBox(b.id)) continue;
      if (f.reelWindowMul) out.reelWindowMul *= f.reelWindowMul;
      if (f.soakMul) out.soakMul *= f.soakMul;
      out.tableTier += f.tableTier ?? 0;
      out.netCount += f.netCount ?? 0;
    }
  }
  return out;
}

/** The folded hunting effects of every unlocked box (plan 11) — the fishingSum
 *  sibling, same identity/zero bases and the same one-copy discipline: the
 *  server's fire/trap handlers and the studio driver both fold through here.
 *  The bow's own `hunting` marker bonuses layer ON TOP at the call site
 *  (aimHitBonus/trophyTier sum with the item's). */
export type HuntingSum = { aimHitBonus: number; trophyTier: number; trapCount: number; soakMul: number };
export function huntingSum(skills: SkillSpec[], hasBox: (boxId: string) => boolean): HuntingSum {
  const out: HuntingSum = { aimHitBonus: 0, trophyTier: 0, trapCount: 0, soakMul: 1 };
  for (const spec of skills) {
    for (const b of spec.boxes) {
      const h = b.grants.hunting;
      if (!h || !hasBox(b.id)) continue;
      if (h.soakMul) out.soakMul *= h.soakMul;
      out.aimHitBonus += h.aimHitBonus ?? 0;
      out.trophyTier += h.trophyTier ?? 0;
      out.trapCount += h.trapCount ?? 0;
    }
  }
  return out;
}

/** The folded cooking effects of every unlocked box (plan 18/25) — the
 *  fishingSum sibling, same identity/zero bases and the same one-copy
 *  discipline: the server's cook/brew handlers, the client's slot UX, and the
 *  studio driver all fold through here. `*Mul` fields multiply, the rest sum.
 *  `panWindowMul` widens the pan minigame's prompt hit window (the
 *  reelWindowMul twin). */
export type CookingSum = { panWindowMul: number; healBonus: number; brewCount: number; soakMul: number };
export function cookingSum(skills: SkillSpec[], hasBox: (boxId: string) => boolean): CookingSum {
  const out: CookingSum = { panWindowMul: 1, healBonus: 0, brewCount: 0, soakMul: 1 };
  for (const spec of skills) {
    for (const b of spec.boxes) {
      const c = b.grants.cooking;
      if (!c || !hasBox(b.id)) continue;
      if (c.panWindowMul) out.panWindowMul *= c.panWindowMul;
      if (c.soakMul) out.soakMul *= c.soakMul;
      out.healBonus += c.healBonus ?? 0;
      out.brewCount += c.brewCount ?? 0;
    }
  }
  return out;
}

/** THE single box-eligibility rule, shared by the server's unlockBox validation and the
 *  client's box-state render so they can never drift. Gates checked in requirement order;
 *  the first failure is returned. `ok: true` ⇒ affordable + eligible right now. */
export function checkBox(spec: SkillSpec, box: SkillBox, p: BoxProgress): BoxCheck {
  if (p.hasBox(box.id)) return { ok: false, gate: "unlocked" };
  const r = box.requires;
  if (p.xp(r.bar) < r.xp) return { ok: false, gate: "xp", bar: r.bar, need: r.xp };
  const missing = r.prereq?.find((id) => !p.hasBox(id));
  if (missing) return { ok: false, gate: "prereq", prereq: missing };
  if (r.rank && p.standingRank(r.rank.track) < r.rank.rank) return { ok: false, gate: "rank", track: r.rank.track, rank: r.rank.rank };
  if (p.influence < box.cost) return { ok: false, gate: "influence" };
  if (p.invested + box.cost > spec.capBoxes) return { ok: false, gate: "cap" };
  return { ok: true };
}
